/**
 * 보호자 활동 로그 페이지
 * - 보호자 콤보박스 (전체 / 특정 보호자)
 * - 기간 picker
 * - 카테고리(결제/이용권/알림장) 필터
 * - 컬럼: 로그일시 / 작업 수행자(보호자) / 변경내용 / 카테고리
 */

import { useEffect, useMemo, useState } from 'react';
import type { ColDef, ICellRendererParams } from 'ag-grid-community';
import {
  useGuardianActivityLogs,
  useGuardianOptions,
} from '@/hooks/useActivityLogs';
import {
  GUARDIAN_EVENT_TO_CATEGORY,
  GUARDIAN_LOG_CATEGORY_LABEL,
  type GuardianActivityLog,
  type GuardianLogCategory,
} from '@/services/api';
import {
  PageHeader,
  FilterBar,
  FilterField,
  TextInput,
  Select,
  Pagination,
  StatusBadge,
  type BadgeTone,
} from '@/components/common';
import { DataGrid, RowNumberCell } from '@/components/grid';
import { formatGuardianLog } from './formatGuardianLog';

const PAGE_SIZE_OPTIONS = [20, 50, 100];

const CATEGORY_ORDER: GuardianLogCategory[] = ['PAYMENT', 'TICKET', 'NOTICE'];

const CATEGORY_TONE: Record<GuardianLogCategory, BadgeTone> = {
  PAYMENT: 'success',
  TICKET: 'primary',
  NOTICE: 'info',
};

function getDefaultRange(): { from: string; to: string } {
  const pad = (n: number) => String(n).padStart(2, '0');
  const fmt = (d: Date) =>
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  const today = new Date();
  const from = new Date();
  from.setDate(today.getDate() - 30);
  return { from: fmt(from), to: fmt(today) };
}

export function GuardianActivityLogPage() {
  const defaultRange = useMemo(getDefaultRange, []);

  const [localUserSeq, setLocalUserSeq] = useState<string>('');
  const [localDateFrom, setLocalDateFrom] = useState(defaultRange.from);
  const [localDateTo, setLocalDateTo] = useState(defaultRange.to);
  const [localCategories, setLocalCategories] = useState<
    Set<GuardianLogCategory>
  >(new Set());
  const [localSize, setLocalSize] = useState(20);

  const [applied, setApplied] = useState({
    userSeq: undefined as number | undefined,
    dateFrom: defaultRange.from,
    dateTo: defaultRange.to,
    categories: [] as GuardianLogCategory[],
  });
  const [pageIndex, setPageIndex] = useState(0);
  const [size, setSize] = useState(20);
  const [refetchNonce, setRefetchNonce] = useState(0);

  const { data: guardianOptions = [], isLoading: optionsLoading } =
    useGuardianOptions();

  const { data, isLoading, isFetching, error, refetch } =
    useGuardianActivityLogs(
      {
        userSeq: applied.userSeq,
        dateFrom: applied.dateFrom,
        dateTo: applied.dateTo,
        categories:
          applied.categories.length > 0 ? applied.categories : undefined,
        page: pageIndex,
        size,
      },
      refetchNonce,
    );

  const rows = data?.content ?? [];
  const totalPages = data?.totalPages ?? 0;
  const totalElements = data?.totalElements ?? 0;
  const startIndex = pageIndex * size;

  useEffect(() => {
    setPageIndex(0);
  }, [applied, size]);

  const selectedGuardian = useMemo(
    () =>
      applied.userSeq != null
        ? guardianOptions.find(g => g.userSeq === applied.userSeq)
        : undefined,
    [guardianOptions, applied.userSeq],
  );

  const applyFilters = () => {
    setApplied({
      userSeq: localUserSeq ? Number(localUserSeq) : undefined,
      dateFrom: localDateFrom,
      dateTo: localDateTo,
      categories: Array.from(localCategories),
    });
    setSize(localSize);
    setRefetchNonce(n => n + 1);
  };

  const resetFilters = () => {
    setLocalUserSeq('');
    setLocalDateFrom(defaultRange.from);
    setLocalDateTo(defaultRange.to);
    setLocalCategories(new Set());
    setLocalSize(20);
    setApplied({
      userSeq: undefined,
      dateFrom: defaultRange.from,
      dateTo: defaultRange.to,
      categories: [],
    });
    setSize(20);
    setRefetchNonce(n => n + 1);
  };

  const toggleCategory = (c: GuardianLogCategory) => {
    setLocalCategories(prev => {
      const next = new Set(prev);
      if (next.has(c)) next.delete(c);
      else next.add(c);
      return next;
    });
  };

  const columns = useMemo<ColDef<GuardianActivityLog>[]>(
    () => [
      {
        headerName: '번호',
        width: 72,
        sortable: false,
        cellRenderer: RowNumberCell,
        cellRendererParams: { startIndex },
      },
      {
        headerName: '로그 일시',
        field: 'dtLog',
        width: 170,
        valueFormatter: p =>
          p.value ? String(p.value).replace('T', ' ') : '-',
      },
      {
        headerName: '카테고리',
        width: 110,
        valueGetter: p =>
          p.data ? GUARDIAN_EVENT_TO_CATEGORY[p.data.eventCode] : '',
        cellRenderer: (p: ICellRendererParams<GuardianActivityLog>) => {
          if (!p.data) return '-';
          const cat = GUARDIAN_EVENT_TO_CATEGORY[p.data.eventCode];
          return (
            <StatusBadge tone={CATEGORY_TONE[cat]}>
              {GUARDIAN_LOG_CATEGORY_LABEL[cat]}
            </StatusBadge>
          );
        },
      },
      {
        headerName: '작업 수행자',
        width: 150,
        valueGetter: p => p.data?.userName ?? '',
        cellRenderer: (p: ICellRendererParams<GuardianActivityLog>) => {
          if (!p.data) return '-';
          return (
            <span className="inline-flex items-center gap-1.5">
              <span className="font-medium text-gray-900">
                {p.data.userName}
              </span>
              <span className="text-xs text-gray-400">
                #{p.data.userSeq}
              </span>
            </span>
          );
        },
      },
      {
        headerName: '변경 내용',
        flex: 1,
        minWidth: 320,
        autoHeight: true,
        cellRenderer: (p: ICellRendererParams<GuardianActivityLog>) => {
          if (!p.data) return '-';
          return (
            <div className="whitespace-normal py-1 leading-relaxed">
              {formatGuardianLog(p.data)}
            </div>
          );
        },
      },
    ],
    [startIndex],
  );

  return (
    <div className="space-y-4">
      <PageHeader
        title="보호자 활동 로그"
        subtitle="보호자별 도메인 이벤트(결제/이용권/알림장) 활동 기록"
      />

      {/* 선택된 보호자 헤더 */}
      <div className="rounded-lg border border-gray-200 bg-white px-4 py-3">
        <div className="text-xs text-gray-500">조회 대상 보호자</div>
        <div className="mt-1 flex items-center gap-2">
          {selectedGuardian ? (
            <>
              <span className="text-lg font-bold text-gray-900">
                {selectedGuardian.userName}
              </span>
              <span className="text-sm text-gray-500">
                · #{selectedGuardian.userSeq} · ****-{selectedGuardian.phoneTail}
              </span>
            </>
          ) : (
            <span className="text-lg font-bold text-gray-700">
              전체 보호자 ({guardianOptions.length}명)
            </span>
          )}
        </div>
      </div>

      {/* 필터 */}
      <FilterBar onSubmit={applyFilters} onReset={resetFilters}>
        <FilterField label="보호자" flex="flex-none" className="md:w-64">
          <Select
            value={localUserSeq}
            onChange={e => setLocalUserSeq(e.target.value)}
            disabled={optionsLoading}
          >
            <option value="">전체 보호자</option>
            {guardianOptions.map(g => (
              <option key={g.userSeq} value={g.userSeq}>
                {g.userName} (****-{g.phoneTail})
              </option>
            ))}
          </Select>
        </FilterField>
        <FilterField label="기간 시작" flex="flex-none" className="md:w-44">
          <TextInput
            type="date"
            value={localDateFrom}
            onChange={e => setLocalDateFrom(e.target.value)}
          />
        </FilterField>
        <FilterField label="기간 종료" flex="flex-none" className="md:w-44">
          <TextInput
            type="date"
            value={localDateTo}
            onChange={e => setLocalDateTo(e.target.value)}
          />
        </FilterField>
        <FilterField label="페이지 크기" flex="flex-none" className="md:w-24">
          <Select
            value={localSize}
            onChange={e => setLocalSize(Number(e.target.value))}
          >
            {PAGE_SIZE_OPTIONS.map(n => (
              <option key={n} value={n}>
                {n}개
              </option>
            ))}
          </Select>
        </FilterField>
      </FilterBar>

      {/* 카테고리 다중 선택 */}
      <div className="rounded-lg border border-gray-200 bg-white p-3">
        <div className="mb-2 flex items-center justify-between">
          <div className="text-xs font-medium text-gray-600">
            카테고리 필터{' '}
            <span className="text-gray-400">(선택 없음 = 전체)</span>
          </div>
          {localCategories.size > 0 && (
            <button
              type="button"
              className="text-xs text-primary-600 hover:underline"
              onClick={() => setLocalCategories(new Set())}
            >
              선택 해제
            </button>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          {CATEGORY_ORDER.map(c => {
            const checked = localCategories.has(c);
            return (
              <label
                key={c}
                className={`inline-flex cursor-pointer items-center gap-1.5 rounded-full border px-3 py-1 text-xs transition-colors ${
                  checked
                    ? 'border-primary-500 bg-primary-50 text-primary-700'
                    : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                <input
                  type="checkbox"
                  className="hidden"
                  checked={checked}
                  onChange={() => toggleCategory(c)}
                />
                {GUARDIAN_LOG_CATEGORY_LABEL[c]}
              </label>
            );
          })}
        </div>
      </div>

      <DataGrid<GuardianActivityLog>
        columns={columns}
        data={rows}
        loading={isLoading}
        fetching={isFetching && !isLoading}
        error={error}
        onRetry={() => refetch()}
        getRowId={row => String(row.logSeq)}
        emptyMessage="조건에 맞는 활동 로그가 없습니다."
      />

      <Pagination
        pageIndex={pageIndex}
        totalPages={totalPages}
        totalElements={totalElements}
        pageSize={size}
        onPageChange={setPageIndex}
      />
    </div>
  );
}
